/**
 * Prompt Builder Presets Configuration
 * Defines all dropdown options and preset values for the prompt builder
 */

export const QUALITY_PRESETS = [
  {
    id: 'vhs',
    name: 'VHS',
    description: '1990s camcorder, VHS artifacts, tracking lines',
    prefix: 'VHS aesthetic, consumer camcorder look, slight tracking artifacts, warm analog tones',
    negativeDefault: 'clean digital look, perfect stabilization, modern smartphones, cinematic film lighting',
  },
  {
    id: 'super8',
    name: 'Super 8',
    description: 'Vintage 8mm film, heavy grain, light leaks',
    prefix: 'Super 8mm film aesthetic, heavy grain texture, occasional light leaks, vintage color palette',
    negativeDefault: 'digital, clean, sharp, modern, HD',
  },
  {
    id: 'film35',
    name: '35mm Film',
    description: 'Classic film grain, organic texture',
    prefix: '35mm film photography, organic film grain, natural color science, slight film halation',
    negativeDefault: 'digital noise, over-sharpened, artificial',
  },
  {
    id: 'polaroid',
    name: 'Polaroid',
    description: 'Instant film, faded colors, soft edges',
    prefix: 'Polaroid instant film aesthetic, faded colors, soft vignette, slightly washed out highlights',
    negativeDefault: 'sharp, high contrast, vivid colors, digital',
  },
  {
    id: 'clean',
    name: 'Clean Digital',
    description: 'Modern digital camera, sharp, clean',
    prefix: 'Clean digital photography, sharp focus, accurate colors',
    negativeDefault: 'grain, artifacts, blur, noise',
  },
  {
    id: '4k-cinema',
    name: '4K Cinema',
    description: 'Cinematic look, color graded',
    prefix: '4K cinematic quality, professional color grading, anamorphic lens characteristics',
    negativeDefault: 'amateur, low quality, noisy, flat lighting',
  },
  {
    id: '8k-ultra',
    name: '8K Ultra',
    description: 'Ultra-high resolution, extreme detail',
    prefix: '8K ultra high resolution, extremely detailed, sharp, professional photography',
    negativeDefault: 'low resolution, blurry, soft, pixelated',
  },
  {
    id: 'broadcast',
    name: 'Broadcast TV',
    description: '1990s TV broadcast quality',
    prefix: 'Broadcast television quality circa 1990s, interlaced video feel, studio lighting',
    negativeDefault: 'film look, cinematic, modern HD',
  },
]

export const ASPECT_RATIOS = [
  { id: '4:3', name: '4:3', description: 'Classic TV / VHS' },
  { id: '16:9', name: '16:9', description: 'Widescreen HD' },
  { id: '1:1', name: '1:1', description: 'Square' },
  { id: '21:9', name: '21:9', description: 'Ultra-wide Cinema' },
  { id: '9:16', name: '9:16', description: 'Vertical / Mobile' },
  { id: '3:2', name: '3:2', description: '35mm Film' },
  { id: '2.39:1', name: '2.39:1', description: 'Anamorphic' },
]

export const CAMERA_ANGLES = [
  { id: 'eye-level', name: 'Eye Level', description: 'Neutral, natural perspective' },
  { id: 'low-angle', name: 'Low Angle', description: 'Looking up, empowering' },
  { id: 'high-angle', name: 'High Angle', description: 'Looking down, diminishing' },
  { id: 'dutch', name: 'Dutch Angle', description: 'Tilted, unsettling' },
  { id: 'birds-eye', name: "Bird's Eye", description: 'Directly overhead' },
  { id: 'worms-eye', name: "Worm's Eye", description: 'Extreme low, looking up' },
  { id: 'pov', name: 'POV', description: 'First person perspective' },
  { id: 'ots', name: 'Over the Shoulder', description: 'Behind character' },
]

export const FOCAL_LENGTHS = [
  { id: '14mm', name: '14mm', description: 'Ultra Wide' },
  { id: '24mm', name: '24mm', description: 'Wide' },
  { id: '35mm', name: '35mm', description: 'Standard Wide' },
  { id: '50mm', name: '50mm', description: 'Normal' },
  { id: '85mm', name: '85mm', description: 'Portrait' },
  { id: '135mm', name: '135mm', description: 'Telephoto' },
  { id: '200mm', name: '200mm', description: 'Long Telephoto' },
]

export const DEPTH_OF_FIELD = [
  { id: 'shallow', name: 'Shallow', description: 'Blurred background, subject isolation' },
  { id: 'medium', name: 'Medium', description: 'Balanced focus' },
  { id: 'deep', name: 'Deep', description: 'Everything in focus' },
]

export const CAMERA_MOVEMENTS = [
  { id: 'static', name: 'Static', description: 'Locked off, no movement' },
  { id: 'handheld', name: 'Handheld', description: 'Natural jitter and shake' },
  { id: 'dolly', name: 'Dolly', description: 'Smooth forward/back movement' },
  { id: 'tracking', name: 'Tracking', description: 'Following subject sideways' },
  { id: 'pan', name: 'Pan', description: 'Horizontal rotation' },
  { id: 'tilt', name: 'Tilt', description: 'Vertical rotation' },
  { id: 'zoom', name: 'Zoom', description: 'Focal length change' },
  { id: 'crane', name: 'Crane', description: 'Vertical movement up/down' },
  { id: 'whip-pan', name: 'Whip Pan', description: 'Fast horizontal blur' },
]

export const LIGHTING_PRESETS = [
  { id: 'natural', name: 'Natural Light', description: 'Daylight, window light' },
  { id: 'tungsten', name: 'Tungsten', description: 'Warm indoor bulbs' },
  { id: 'fluorescent', name: 'Fluorescent', description: 'Cool, greenish office lighting' },
  { id: 'neon', name: 'Neon', description: 'Colorful neon signs' },
  { id: 'golden-hour', name: 'Golden Hour', description: 'Warm sunset/sunrise' },
  { id: 'blue-hour', name: 'Blue Hour', description: 'Cool twilight' },
  { id: 'dramatic', name: 'Dramatic', description: 'High contrast, shadows' },
  { id: 'flat', name: 'Flat', description: 'Even, soft lighting' },
  { id: 'mixed', name: 'Mixed', description: 'Multiple light sources' },
]

/**
 * Default prompt state for initializing the builder
 */
export const DEFAULT_PROMPT_STATE = {
  sections: {
    quality: { enabled: true, collapsed: false },
    subject: { enabled: true, collapsed: false },
    environment: { enabled: true, collapsed: false },
    cinematography: { enabled: false, collapsed: true },
    action: { enabled: false, collapsed: true },
  },
  values: {
    quality: {
      preset: 'clean',
      aspectRatio: '4:3',
      styleNote: '',
      negativePrompt: '',
      includeNegative: true,
    },
    subject: {
      description: '',
      attire: '',
      facialFeatures: '',
      props: '',
      consistencyAnchor: '',
    },
    environment: {
      location: '',
      lightingGlobal: '',
      lightingAccent: '',
      backgroundDetails: '',
      atmosphere: '',
      consistencyAnchor: '',
    },
    cinematography: {
      cameraAngle: 'eye-level',
      focalLength: '35mm',
      depthOfField: 'medium',
      cameraPosition: '',
      movement: 'static',
    },
    action: {
      pose: '',
      expression: '',
      dynamicElements: '',
      interaction: '',
    },
  },
}

/**
 * Generate the composed prompt string from state
 */
export function generatePrompt(state) {
  const parts = []
  const { sections, values } = state

  // Quality & Style section
  if (sections.quality.enabled) {
    const quality = values.quality
    const preset = QUALITY_PRESETS.find(p => p.id === quality.preset)
    
    if (preset) {
      parts.push(preset.prefix)
    }
    
    const ratio = ASPECT_RATIOS.find(r => r.id === quality.aspectRatio)
    if (ratio) {
      parts.push(`${ratio.name} aspect ratio`)
    }
    
    if (quality.styleNote?.trim()) {
      parts.push(quality.styleNote.trim())
    }
  }

  // Subject section
  if (sections.subject.enabled) {
    const subject = values.subject
    
    if (subject.description?.trim()) {
      parts.push(subject.description.trim())
    }
    if (subject.attire?.trim()) {
      parts.push(`Wearing: ${subject.attire.trim()}`)
    }
    if (subject.facialFeatures?.trim()) {
      parts.push(`Features: ${subject.facialFeatures.trim()}`)
    }
    if (subject.props?.trim()) {
      parts.push(`Props: ${subject.props.trim()}`)
    }
  }

  // Environment section
  if (sections.environment.enabled) {
    const env = values.environment
    
    if (env.location?.trim()) {
      parts.push(`Setting: ${env.location.trim()}`)
    }
    if (env.lightingGlobal?.trim()) {
      parts.push(`Lighting: ${env.lightingGlobal.trim()}`)
    }
    if (env.lightingAccent?.trim()) {
      parts.push(`Accent lighting: ${env.lightingAccent.trim()}`)
    }
    if (env.backgroundDetails?.trim()) {
      parts.push(`Background: ${env.backgroundDetails.trim()}`)
    }
    if (env.atmosphere?.trim()) {
      parts.push(`Atmosphere: ${env.atmosphere.trim()}`)
    }
  }

  // Cinematography section
  if (sections.cinematography.enabled) {
    const cine = values.cinematography
    const cinematographyParts = []
    
    const angle = CAMERA_ANGLES.find(a => a.id === cine.cameraAngle)
    if (angle && angle.id !== 'eye-level') {
      cinematographyParts.push(`${angle.name} shot`)
    }
    
    const focal = FOCAL_LENGTHS.find(f => f.id === cine.focalLength)
    if (focal) {
      cinematographyParts.push(`${focal.name} lens`)
    }
    
    const dof = DEPTH_OF_FIELD.find(d => d.id === cine.depthOfField)
    if (dof && dof.id !== 'medium') {
      cinematographyParts.push(`${dof.name} depth of field`)
    }
    
    const movement = CAMERA_MOVEMENTS.find(m => m.id === cine.movement)
    if (movement && movement.id !== 'static') {
      cinematographyParts.push(`${movement.name} camera`)
    }
    
    if (cine.cameraPosition?.trim()) {
      cinematographyParts.push(cine.cameraPosition.trim())
    }
    
    if (cinematographyParts.length > 0) {
      parts.push(cinematographyParts.join(', '))
    }
  }

  // Action section
  if (sections.action.enabled) {
    const action = values.action
    const actionParts = []
    
    if (action.pose?.trim()) {
      actionParts.push(action.pose.trim())
    }
    if (action.expression?.trim()) {
      actionParts.push(action.expression.trim())
    }
    if (action.dynamicElements?.trim()) {
      actionParts.push(action.dynamicElements.trim())
    }
    if (action.interaction?.trim()) {
      actionParts.push(action.interaction.trim())
    }
    
    if (actionParts.length > 0) {
      parts.push(actionParts.join('. '))
    }
  }

  return parts.filter(Boolean).join('. ')
}

/**
 * Generate the negative prompt string from state
 */
export function generateNegativePrompt(state) {
  const { sections, values } = state
  
  if (!sections.quality.enabled || !values.quality.includeNegative) {
    return ''
  }
  
  const quality = values.quality
  const preset = QUALITY_PRESETS.find(p => p.id === quality.preset)
  
  // Use custom negative prompt if provided, otherwise use preset default
  if (quality.negativePrompt?.trim()) {
    return quality.negativePrompt.trim()
  }
  
  return preset?.negativeDefault || ''
}

